import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useDocuSignAgreement } from "../../../hooks/useDocuSignAgreements";

const DocuSignAgreementDialog = ({
  open,
  onClose,
  onAgreed,
  uid,
  email,
  fullName,
  agreementType,
  orderId = null,
  title = "Agreement",
  description,
  prefillTabs = null,
}) => {
  const { checkAgreement, getSigningUrl, pollEnvelopeStatus, checking, signing, error } =
    useDocuSignAgreement(agreementType);

  const [alreadySigned, setAlreadySigned] = useState(false);
  const [signedAt, setSignedAt] = useState(null);
  const [signingUrl, setSigningUrl] = useState(null);
  const [startError, setStartError] = useState(null);
  const stopPollRef = useRef(null);

  // Check Firestore whenever the dialog opens
  useEffect(() => {
    if (!open || !uid) return;
    setSigningUrl(null);
    setStartError(null);
    setAlreadySigned(false);
    setSignedAt(null);

    checkAgreement(uid).then(({ signed, signedAt: sat }) => {
      if (signed) {
        setAlreadySigned(true);
        setSignedAt(sat);
      }
    });
  }, [open, uid, checkAgreement]);

  // Auto-advance if already signed
  useEffect(() => {
    if (alreadySigned && open) {
      const t = setTimeout(() => onAgreed?.(), 1200);
      return () => clearTimeout(t);
    }
  }, [alreadySigned, open, onAgreed]);

  // Cleanup poll on unmount / close
  useEffect(() => {
    return () => stopPollRef.current?.();
  }, []);

  const handleStartSigning = async () => {
    setStartError(null);
    try {
      const result = await getSigningUrl(uid, email, fullName, prefillTabs, orderId);

      if (result.alreadySigned) {
        setAlreadySigned(true);
        return;
      }

      setSigningUrl(result.signingUrl);

      stopPollRef.current = pollEnvelopeStatus(uid, result.envelopeId, () => {
        onAgreed?.();
      }, orderId);
    } catch (err) {
      setStartError(err.message || "Could not start DocuSign signing. Please try again.");
    }
  };

  const handleClose = () => {
    if (signing) return;
    stopPollRef.current?.();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          height: signingUrl ? "85vh" : "auto",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          fontWeight: 700,
          fontSize: "1.1rem",
          pb: 1,
        }}
      >
        <GavelIcon color="primary" />
        {title}
        <Chip
          icon={<DescriptionOutlinedIcon />}
          label="DocuSign"
          size="small"
          variant="outlined"
          sx={{ ml: "auto", fontSize: "0.7rem" }}
        />
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", flex: 1 }}>

        {/* Loading */}
        {checking && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Checking agreement status…
            </Typography>
          </Box>
        )}

        {/* Already signed */}
        {!checking && alreadySigned && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
            gap={1.5}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 52, color: "success.main" }} />
            <Typography variant="h6" fontWeight={700}>
              Agreement already signed
            </Typography>
            {signedAt && (
              <Typography variant="body2" color="text.secondary">
                Signed on{" "}
                {signedAt.toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Continuing to the next step…
            </Typography>
          </Box>
        )}

        {/* Embedded signing iframe */}
        {!checking && !alreadySigned && signingUrl && (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Complete the agreement below. This page will continue automatically once
              you finish signing.
            </Typography>
            <Box
              sx={{
                flex: 1,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <iframe
                title={`DocuSign — ${title}`}
                src={signingUrl}
                width="100%"
                height="100%"
                style={{ border: "none", minHeight: 500 }}
              />
            </Box>
          </Box>
        )}

        {/* Pre-signing intro */}
        {!checking && !alreadySigned && !signingUrl && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description ||
                "Please read and sign the agreement below via DocuSign before continuing."}
            </Typography>

            {(error || startError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error || startError}
              </Alert>
            )}

            <Box display="flex" justifyContent="flex-end" gap={1.5}>
              <Button onClick={handleClose} disabled={signing}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleStartSigning}
                disabled={signing}
                startIcon={
                  signing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <GavelIcon />
                  )
                }
              >
                {signing ? "Starting…" : "Continue to DocuSign"}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocuSignAgreementDialog;